"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { lessonKey, useLearnProgress } from "./progress";
import { ProgressBar } from "./CourseProgress";

export type CourseOutline = {
  slug: string;
  title: string;
  lessons: { slug: string; title: string }[];
};

/* "Pick up where you left off", built from the ticks this browser has stored.
   Nothing is shown until at least one lesson is marked done, so the hub reads
   the same way for a first-time visitor as it always did. */
export default function ContinueLearning({ courses }: { courses: CourseOutline[] }) {
  const { done } = useLearnProgress();

  const total = courses.reduce((sum, c) => sum + c.lessons.length, 0);
  const completed = courses.reduce(
    (sum, c) => sum + c.lessons.filter((l) => done.has(lessonKey(c.slug, l.slug))).length,
    0,
  );
  if (completed === 0) return null;

  /* The first course that is started but unfinished; otherwise the first one
     not started yet, so finishing a course points at the next one. */
  const started = courses.filter((c) =>
    c.lessons.some((l) => done.has(lessonKey(c.slug, l.slug))),
  );
  const unfinished = started.find((c) =>
    c.lessons.some((l) => !done.has(lessonKey(c.slug, l.slug))),
  );
  const fresh = courses.find((c) => !c.lessons.some((l) => done.has(lessonKey(c.slug, l.slug))));
  const course = unfinished ?? fresh;

  const nextLesson = course
    ? (course.lessons.find((l) => !done.has(lessonKey(course.slug, l.slug))) ?? course.lessons[0])
    : undefined;

  const pct = Math.round((completed / total) * 100);

  return (
    <section className="pb-14" aria-labelledby="continue-heading">
      <div className="learn-container px-6 md:px-12">
        <div
          className="rounded-3xl border p-6 md:p-8"
          style={{ background: "var(--panel-strong)", borderColor: "var(--hairline-strong)" }}
        >
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="min-w-0">
              <p
                id="continue-heading"
                className="font-accent text-[11px] font-medium uppercase tracking-[0.22em] text-eyebrow mb-3"
              >
                {unfinished ? "Pick up where you left off" : "Next up"}
              </p>
              {course && nextLesson ? (
                <>
                  <p className="fluid-h3 font-heading font-semibold mb-1">{nextLesson.title}</p>
                  <p className="text-[14px] text-muted-foreground">{course.title}</p>
                </>
              ) : (
                <p className="fluid-h3 font-heading font-semibold">
                  You&apos;ve finished every lesson. Nice work.
                </p>
              )}
            </div>
            {course && nextLesson ? (
              <Link
                href={`/learn/${course.slug}/${nextLesson.slug}`}
                className="btn-gold px-7 py-3.5 shrink-0"
              >
                {unfinished ? "Continue" : "Start"}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            ) : null}
          </div>

          <div className="mt-7 border-t pt-5" style={{ borderColor: "var(--hairline)" }}>
            <div className="mb-2 flex items-center justify-between text-[12px]">
              <span className="font-semibold">
                {completed} of {total} lessons done across the library
              </span>
              <span className="text-muted-foreground">{pct}%</span>
            </div>
            <ProgressBar value={pct} label={`${completed} of ${total} lessons done`} />
            <p className="mt-3 text-[12px] text-muted-foreground">
              Progress is kept in this browser only, so there is nothing to sign in to.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
